import { Highlight, themes } from "prism-react-renderer";
import { AbsoluteFill, useCurrentFrame } from "remotion";
import { COLORS, FONTS, RULES } from "./theme";
import { revealedLineCount } from "./codepanel-math";

const TRAFFIC = ["#ff5f57", "#febc2e", "#28c840"];

/**
 * Chrome-minimal code panel: filename + traffic lights, line-by-line reveal with
 * an 8px translateX fade. The focus line stays at full opacity on a cyan rail;
 * others dim to 30%. NEVER a typing simulation.
 */
export const CodePanel: React.FC<{
  code: string;
  language?: string;
  filename?: string;
  highlightLines?: number[];
  revealFrames?: number;
  fontSize?: number;
  width?: number;
}> = ({
  code,
  language = "tsx",
  filename = "untitled",
  highlightLines = [],
  revealFrames = 45,
  fontSize = 26,
  width = 1160,
}) => {
  const frame = useCurrentFrame();
  const lines = code.replace(/\n$/, "").split("\n");
  const shown = revealedLineCount(frame, lines.length, revealFrames);

  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <div
        style={{
          width,
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${COLORS.line}`,
          boxShadow: `0 28px 80px -36px #000, 0 0 30px -18px ${COLORS.cyan}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#0B0E13",
            borderBottom: `1px solid ${COLORS.line}`,
            padding: "12px 16px",
          }}
        >
          {TRAFFIC.map((c) => (
            <span
              key={c}
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: c,
              }}
            />
          ))}
          <span
            style={{
              fontFamily: FONTS.mono,
              fontSize: 16,
              color: COLORS.dim,
              marginLeft: 8,
            }}
          >
            {filename}
          </span>
        </div>
        <Highlight code={code} language={language} theme={themes.oneDark}>
          {({ style, tokens, getLineProps, getTokenProps }) => (
            <pre
              style={{
                ...style,
                margin: 0,
                padding: "22px 26px",
                fontFamily: FONTS.mono,
                fontSize,
                lineHeight: 1.7,
                background: "#0E1117",
              }}
            >
              {tokens.map((line, i) => {
                const focus = highlightLines.includes(i + 1);
                const visible = i < shown;
                return (
                  <div
                    key={i}
                    {...getLineProps({ line })}
                    style={{
                      opacity: visible
                        ? focus || highlightLines.length === 0
                          ? 1
                          : RULES.codeDimOpacity
                        : 0,
                      transform: visible ? "translateX(0)" : "translateX(8px)",
                      transition: "none",
                      background: focus ? "rgba(34,224,255,0.10)" : "transparent",
                      boxShadow: focus ? `inset 2px 0 0 ${COLORS.cyan}` : "none",
                      margin: "0 -26px",
                      padding: "0 26px",
                    }}
                  >
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </div>
                );
              })}
            </pre>
          )}
        </Highlight>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
